// src/components/DaGuardareAdesso.js
// ═══════════════════════════════════════════════════════════════════════════
// DA GUARDARE ADESSO — in cima alla Home (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Al posto di «Le sezioni pensate per te» (che si mostravano sempre uguali, come se fosse
// sempre il primo accesso), una sintesi PERSONALE di chi entra, fatta con dati che Nexiora
// ha già, nell'ordine:
//  1. 🔔 notifiche non lette (la campanella): quante e le ultime tre;
//  2. ✅ le tue attività importanti: al massimo 5 tra quelle assegnate a te, scelte con
//     regole semplici e sempre uguali, ognuna con il motivo scritto accanto:
//       in ritardo · scade oggi o domani · priorità alta · assegnata da un collega negli
//       ultimi 3 giorni · commessa in consegna entro 7 giorni (o già oltre) · ferma da più
//       di 14 giorni;
//  3. ⏳ cose che aspettano te, solo se ce ne sono e solo per chi può aprire quella pagina:
//     cartellini, ferie e permessi, note spese da approvare, richieste al magazzino in
//     attesa, segnalazioni e richieste commerciali assegnate a te;
//  4. 📝 moduli non salvati su questo dispositivo (Le mie bozze).
// Ogni attività e ogni notifica legata a una commessa porta sotto il riferimento completo
// (W398): 👤 cliente · 📁 numero e nome commessa · ⚡ impianto. L'impianto si scrive solo
// quando la commessa ne ha UNO collegato (scheda Manutenzione): con più impianti l'attività
// non dice a quale si riferisce, e scriverne uno a caso sarebbe sbagliato.
// La commessa si legge dall'elenco della Home; se lì manca (archiviata, cartella cliente,
// non ancora arrivata) si legge direttamente, così il riferimento non sparisce mai.
// Se non c'è niente: una riga «Tutto in ordine per oggi».
// Nessun dato nuovo, nessuna scrittura: solo letture (le stesse di barra laterale e pagine).
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useCanSee } from "../hooks/useCanSee";
import { useBozzeCon } from "../utils/bozzeLocali";
import { toLocalDateStr } from "../utils/dateUtils";
import { caricaIncompleti } from "../utils/giustificazioniPresenze";
import { dataLungaInizio } from "../utils/obbligoPresenze";
import { eFeriale } from "../utils/periodoLavorativo";

export const MAX_ATTIVITA = 5;
export const GIORNI_NUOVA = 3;
export const GIORNI_FERMA = 14;
export const GIORNI_CONSEGNA = 7;

const ms = (v) => (v?.toMillis ? v.toMillis() : v?.seconds ? v.seconds * 1000 : typeof v === "number" ? v : v ? Date.parse(v) || 0 : 0);
const giorniTraIso = (a, b) => Math.round((Date.parse(b + "T00:00:00") - Date.parse(a + "T00:00:00")) / 86400000);
const dataBreve = (iso) => (iso ? iso.slice(8, 10) + "/" + iso.slice(5, 7) : "");
const STATI_CHIUSI = new Set(["completata", "annullata", "sospesa"]);

/**
 * Sceglie e ordina le attività importanti. Funzione pura (provata a parte):
 * tasks = attività assegnate all'utente; progetti = { [id]: commessa }; oggi = "AAAA-MM-GG".
 * Restituisce [{ task, punteggio, motivi: [testo…] }] già ordinate, al massimo MAX_ATTIVITA.
 */
export function attivitaImportanti(tasks, { uid, progetti = {}, oggi, adesso = Date.now() }) {
  const scelte = [];
  (tasks || []).forEach(t => {
    if (!t || STATI_CHIUSI.has(t.status) || t.archived) return;
    const motivi = []; let punti = 0;
    const aggiungi = (p, testo) => { motivi.push(testo); punti = Math.max(punti, p) + (punti ? 5 : 0); };
    const due = typeof t.dueDate === "string" && /^\d{4}-\d{2}-\d{2}/.test(t.dueDate) ? t.dueDate.slice(0, 10) : null;
    if (due) {
      const gg = giorniTraIso(oggi, due);
      if (gg < 0) aggiungi(1000 - gg, `in ritardo di ${-gg} ${-gg === 1 ? "giorno" : "giorni"}`);
      else if (gg === 0) aggiungi(800, "scade oggi");
      else if (gg === 1) aggiungi(790, "scade domani");
    }
    if (t.priority === "alta") aggiungi(600, due && giorniTraIso(oggi, due) > 1 ? `priorità alta · scade il ${dataBreve(due)}` : "priorità alta");
    const creata = ms(t.createdAt);
    if (t.createdBy && t.createdBy !== uid && creata && adesso - creata <= GIORNI_NUOVA * 86400000) {
      const chi = t.createdByName ? `da ${t.createdByName}` : "da un collega";
      const quando = toLocalDateStr(new Date(creata)) === oggi ? "oggi" : `il ${dataBreve(toLocalDateStr(new Date(creata)))}`;
      aggiungi(500, `assegnata ${chi} ${quando}`);
    }
    const p = t.projectId ? progetti[t.projectId] : null;
    if (p && p.statoCommessa !== "chiusa" && typeof p.dataFine === "string" && p.dataFine) {
      const gg = giorniTraIso(oggi, p.dataFine.slice(0, 10));
      if (gg < 0) aggiungi(450, "la commessa ha superato la data di consegna");
      else if (gg <= GIORNI_CONSEGNA) aggiungi(400, `commessa in consegna il ${dataBreve(p.dataFine)}`);
    }
    const mosso = ms(t.updatedAt) || creata;
    if (mosso && adesso - mosso > GIORNI_FERMA * 86400000) {
      aggiungi(300, `ferma da ${Math.floor((adesso - mosso) / 86400000)} giorni`);
    }
    if (motivi.length) scelte.push({ task: t, punteggio: punti, motivi });
  });
  return scelte.sort((a, b) => b.punteggio - a.punteggio).slice(0, MAX_ATTIVITA);
}

// Cose che aspettano: [chiave pagina, testo singolare/plurale, conteggio]
const PAGINE = ["approvazione-cartellini", "approvazione-ferie-permessi", "approvazione-note-spese", "magazzino"];
function useCoseCheAspettano(orgId, uid, canSee) {
  const [voci, setVoci] = useState([]);
  // canSee cambia a ogni disegno: si usano i soli sì/no che servono (stringa stabile)
  const permessi = PAGINE.map(p => (canSee(p) ? "1" : "0")).join("");
  useEffect(() => {
    const vede = (p) => permessi[PAGINE.indexOf(p)] === "1";
    if (!orgId || !uid) { setVoci([]); return undefined; }
    let vivo = true;
    // Ogni lettura chiede al database SOLO i documenti che contano (filtri sullo stato o
    // sull'assegnatario), non l'intera raccolta dell'azienda: la Home si apre spesso e le
    // letture del database si pagano. Filtri solo di uguaglianza: nessun indice da creare.
    const conta = async (q, filtro) => { try { const s = await getDocs(q); return s.docs.map(d => d.data()).filter(filtro).length; } catch { return 0; } };
    const lavori = [];
    if (vede("approvazione-cartellini")) lavori.push(["approvazione-cartellini", ["cartellino da approvare", "cartellini da approvare"],
      conta(query(collection(db, "presenze"), where("orgId", "==", orgId), where("stato", "==", "inviata")), () => true)]);
    if (vede("approvazione-cartellini")) lavori.push(["approvazione-cartellini", ["giustificazione di una timbratura da decidere", "giustificazioni di timbrature da decidere"],
      conta(query(collection(db, "giustificazioniPresenze"), where("orgId", "==", orgId), where("stato", "==", "inviata")), () => true)]);
    if (vede("approvazione-ferie-permessi")) lavori.push(["approvazione-ferie-permessi", ["richiesta di ferie o permesso da approvare", "richieste di ferie o permessi da approvare"],
      conta(query(collection(db, "richiesteFerie"), where("organizationId", "==", orgId), where("stato", "==", "in_attesa")), () => true)]);
    if (vede("approvazione-note-spese")) lavori.push(["approvazione-note-spese", ["nota spese da approvare", "note spese da approvare"],
      conta(query(collection(db, "noteSpese"), where("orgId", "==", orgId), where("stato", "==", "inviata")), () => true)]);
    if (vede("magazzino")) lavori.push(["magazzino", ["richiesta al magazzino in attesa", "richieste al magazzino in attesa"],
      conta(query(collection(db, "richiesteMateriali"), where("organizationId", "==", orgId), where("stato", "==", "in_attesa")), () => true)]);
    lavori.push(["segnalazioni-cliente", ["segnalazione cliente assegnata a te", "segnalazioni cliente assegnate a te"],
      conta(query(collection(db, "segnalazioniCliente"), where("orgId", "==", orgId), where("assegnatarioUid", "==", uid)), s => s.stato !== "chiusa")]);
    lavori.push(["registro-richieste-comm", ["richiesta commerciale assegnata a te", "richieste commerciali assegnate a te"],
      conta(query(collection(db, "richiesteCommerciali"), where("orgId", "==", orgId), where("assegnatarioUid", "==", uid)), r => r.stato !== "annullata")]);
    Promise.all(lavori.map(l => l[2])).then(numeri => {
      if (!vivo) return;
      setVoci(lavori.map((l, i) => ({ chiave: `${l[0]}-${i}`, pagina: l[0], testo: l[1], n: numeri[i] })).filter(v => v.n > 0));
    });
    return () => { vivo = false; };
  }, [orgId, uid, permessi]);
  return voci;
}

// Timbrature dimenticate (W419): chi approva i cartellini vede chi, l'ultimo giorno feriale,
// doveva timbrare e non ha la timbratura completa né una giustificazione. Solo se l'azienda
// ha acceso l'obbligo (Impostazioni → Presenze e approvazioni).
// la Home si apre spesso: il conteggio (una lettura per persona) si rifà al massimo ogni 10 minuti
const cacheIncompleti = new Map();
function useIncompletiIeri(orgId, attivo) {
  const [esito, setEsito] = useState(null);
  useEffect(() => {
    if (!orgId || !attivo) { setEsito(null); return undefined; }
    let vivo = true;
    const oggi = new Date();
    const giorno = new Date(oggi.getFullYear(), oggi.getMonth(), oggi.getDate() - 1);
    while (!eFeriale(giorno)) giorno.setDate(giorno.getDate() - 1);
    const chiave = `${orgId}|${giorno.toDateString()}`;
    const c = cacheIncompleti.get(chiave);
    if (c && Date.now() - c.t < 600000) { setEsito(c.esito); return () => { vivo = false; }; }
    caricaIncompleti({ orgId, giorno })
      .then(r => {
        const e = r && r.persone.length ? { quando: dataLungaInizio(giorno), persone: r.persone } : null;
        cacheIncompleti.set(chiave, { t: Date.now(), esito: e });
        if (vivo) setEsito(e);
      })
      .catch(() => { if (vivo) setEsito(null); });
    return () => { vivo = false; };
  }, [orgId, attivo]);
  return esito;
}

/**
 * Riferimenti delle commesse citate (cliente, numero, nome, impianto unico).
 * ids = commesse da descrivere; mappa = commesse già lette dalla Home.
 */
function useRiferimenti(ids, mappa, orgId) {
  const chiave = [...new Set(ids.filter(Boolean))].sort().join(",");
  const [mancanti, setMancanti] = useState({});   // commesse lette a parte
  const [impianti, setImpianti] = useState({});   // projectId → nomi degli impianti collegati
  const mappaRef = useRef(mappa);
  mappaRef.current = mappa;
  useEffect(() => {
    if (!chiave || !orgId) return undefined;
    let vivo = true;
    chiave.split(",").forEach(id => {
      if (!mappaRef.current[id]) getDoc(doc(db, "projects", id)).then(s => {
        if (vivo && s.exists()) setMancanti(m => ({ ...m, [id]: { id, ...s.data() } }));
      }).catch(() => {});
      getDocs(query(collection(db, "manutenzioneImpianti"), where("organizationId", "==", orgId), where("projectId", "==", id)))
        .then(s => { if (vivo) setImpianti(m => ({ ...m, [id]: s.docs.map(d => d.data().nome).filter(Boolean) })); })
        .catch(() => {});
    });
    return () => { vivo = false; };
  }, [chiave, orgId]);
  return (id) => {
    if (!id) return null;
    const p = mappa[id] || mancanti[id];
    if (!p) return null;
    const imp = impianti[id] || [];
    return { cliente: p.clienteNome || "", codice: p.codiceCommessa || "", nome: p.name || "", impianto: imp.length === 1 ? imp[0] : "" };
  };
}

/** 👤 cliente · 📁 AI-26-031 Nome commessa · ⚡ impianto */
export function Riferimento({ r }) {
  if (!r) return null;
  const parti = [
    r.cliente && <span key="c">👤 {r.cliente}</span>,
    (r.codice || r.nome) && <span key="p">📁 {r.codice && <b style={{ color: "#6d28d9" }}>{r.codice}</b>}{r.codice && r.nome ? " " : ""}{r.nome}</span>,
    r.impianto && <span key="i">⚡ {r.impianto}</span>,
  ].filter(Boolean);
  if (!parti.length) return null;
  return (
    <span style={{ display: "flex", flexWrap: "wrap", gap: "2px 12px", fontSize: 11.5, color: "#4b5563", marginTop: 3 }}>{parti}</span>
  );
}

const riga = { display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "#fff", border: "1px solid #ede9fe",
  borderRadius: 9, padding: "8px 11px", marginTop: 6, cursor: "pointer", fontFamily: "'Inter',sans-serif", fontSize: 13, color: "#111827" };
const titoloSezione = { fontSize: 12, fontWeight: 800, color: "#6d28d9", textTransform: "uppercase", letterSpacing: ".04em", marginTop: 12 };

export default function DaGuardareAdesso({ onNavigate, progetti = [] }) {
  const { currentUser, isOrgAdmin } = useAuth();
  const { effectiveOrgId } = useOrg();
  const canSee = useCanSee();
  const uid = currentUser?.uid;
  const [tasks, setTasks] = useState(null);
  const [notifiche, setNotifiche] = useState([]);
  const aspettano = useCoseCheAspettano(effectiveOrgId, uid, canSee);
  const incompleti = useIncompletiIeri(effectiveOrgId, isOrgAdmin || canSee("approvazione-cartellini"));
  const bozze = useBozzeCon(`${uid || "?"}|`);
  // 📌 note importanti (rosse) da tenere sotto osservazione (W406)
  const [noteRosse, setNoteRosse] = useState([]);
  useEffect(() => {
    if (!uid || !effectiveOrgId) return undefined;
    return onSnapshot(query(collection(db, "noteAppunti"), where("uid", "==", uid), where("colore", "==", "rosso")),
      s => setNoteRosse(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(n => !n.archiviata && n.orgId === effectiveOrgId && String(n.testo || "").trim())
        .sort((a, b) => ms(b.updatedAt || b.createdAt) - ms(a.updatedAt || a.createdAt))), () => setNoteRosse([]));
  }, [uid, effectiveOrgId]);

  useEffect(() => {
    if (!uid || !effectiveOrgId) return undefined;
    return onSnapshot(query(collection(db, "tasks"), where("assignedTo", "==", uid), where("organizationId", "==", effectiveOrgId)),
      s => setTasks(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setTasks([]));
  }, [uid, effectiveOrgId]);
  useEffect(() => {
    if (!uid || !effectiveOrgId) return undefined;
    return onSnapshot(query(collection(db, "notifiche"), where("userId", "==", uid), where("orgId", "==", effectiveOrgId)),
      s => setNotifiche(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(n => n.letta !== true).sort((a, b) => ms(b.createdAt) - ms(a.createdAt))),
      () => setNotifiche([]));
  }, [uid, effectiveOrgId]);

  const mappaProgetti = useMemo(() => Object.fromEntries((progetti || []).map(p => [p.id, p])), [progetti]);
  const importanti = useMemo(() => attivitaImportanti(tasks || [], { uid, progetti: mappaProgetti, oggi: toLocalDateStr(new Date()) }), [tasks, uid, mappaProgetti]);
  const rif = useRiferimenti([...importanti.map(x => x.task.projectId), ...notifiche.slice(0, 3).map(n => n.projectId)], mappaProgetti, effectiveOrgId);

  if (tasks === null) return null; // ancora in caricamento: niente lampeggi
  const vuoto = !notifiche.length && !importanti.length && !aspettano.length && !incompleti && !bozze.length && !noteRosse.length;

  return (
    <div style={{ background: "linear-gradient(135deg,#faf5ff,#fff)", border: "1px solid #e9d5ff", borderRadius: 14, padding: "14px 16px", marginBottom: 18 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: "#1e1b4b" }}>👀 Da guardare adesso</div>
      {vuoto && <div style={{ fontSize: 13, color: "#059669", fontWeight: 600, marginTop: 6 }}>Tutto in ordine per oggi ✅</div>}

      {notifiche.length > 0 && (<>
        <div style={titoloSezione}>🔔 {notifiche.length} {notifiche.length === 1 ? "notifica non letta" : "notifiche non lette"}</div>
        {notifiche.slice(0, 3).map(n => (
          <button key={n.id} type="button" style={riga} onClick={() => onNavigate({ page: "notifications" })}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <strong>{n.titolo || "Notifica"}</strong>
              {n.testo ? <span style={{ color: "#6b7280" }}> — {String(n.testo).slice(0, 90)}</span> : null}
              <Riferimento r={rif(n.projectId)} />
            </span>
          </button>
        ))}
        {notifiche.length > 3 && (
          <button type="button" onClick={() => onNavigate({ page: "notifications" })} style={{ background: "none", border: "none", color: "#7c3aed", fontWeight: 700, fontSize: 12, cursor: "pointer", padding: "6px 0 0", fontFamily: "'Inter',sans-serif" }}>
            Vedi tutte le notifiche →
          </button>
        )}
      </>)}

      {importanti.length > 0 && (<>
        <div style={titoloSezione}>✅ Le tue attività importanti</div>
        {importanti.map(({ task: t, motivi }) => {
          const rosso = motivi[0].startsWith("in ritardo") || motivi[0].startsWith("scade");
          return (
            <button key={t.id} type="button" style={{ ...riga, borderLeft: `4px solid ${rosso ? "#dc2626" : "#7c3aed"}` }}
              onClick={() => onNavigate(t.projectId ? { page: "project", projectId: t.projectId, taskId: t.id } : { page: "my-tasks" })}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{t.title || t.titolo || "Attività"}</strong>
                <span style={{ display: "block", fontSize: 12, color: rosso ? "#b91c1c" : "#6d28d9", marginTop: 1 }}>
                  {motivi.join(" · ")}
                </span>
                <Riferimento r={rif(t.projectId)} />
              </span>
              <span style={{ color: "#a78bfa" }}>›</span>
            </button>
          );
        })}
      </>)}

      {noteRosse.length > 0 && (<>
        <div style={titoloSezione}>📌 Note importanti</div>
        {noteRosse.slice(0, 3).map(n => (
          <button key={n.id} type="button" style={{ ...riga, borderLeft: "4px solid #dc2626", background: "#fef2f2" }} onClick={() => onNavigate({ page: "registro-note" })}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(n.testo).replace(/\s+/g, " ").trim()}</span>
              {(n.codiceCommessa || n.projectName) ? <span style={{ display: "block", fontSize: 11.5, color: "#6b7280", marginTop: 2 }}>📁 {n.codiceCommessa ? <b style={{ color: "#6d28d9" }}>{n.codiceCommessa}</b> : null} {n.projectName || ""}</span> : null}
            </span>
            <span style={{ color: "#f87171" }}>›</span>
          </button>
        ))}
        {noteRosse.length > 3 && <button type="button" onClick={() => onNavigate({ page: "registro-note" })} style={{ background: "none", border: "none", color: "#7c3aed", fontWeight: 700, fontSize: 12, cursor: "pointer", padding: "6px 0 0", fontFamily: "'Inter',sans-serif" }}>Tutte le note importanti ({noteRosse.length}) →</button>}
      </>)}

      {(aspettano.length > 0 || incompleti) && (<>
        <div style={titoloSezione}>⏳ Aspettano te</div>
        {incompleti && (
          <button type="button" style={riga} onClick={() => onNavigate({ page: "presenze" })}>
            <span style={{ flex: 1 }}>🕐 {incompleti.quando} senza timbratura completa: <strong>{incompleti.persone.map(p => p.nome).join(", ")}</strong></span>
            <span style={{ color: "#a78bfa" }}>›</span>
          </button>
        )}
        {aspettano.map(v => (
          <button key={v.chiave} type="button" style={riga} onClick={() => onNavigate({ page: v.pagina })}>
            <span style={{ flex: 1 }}><strong>{v.n}</strong> {v.n === 1 ? v.testo[0] : v.testo[1]}</span>
            <span style={{ color: "#a78bfa" }}>›</span>
          </button>
        ))}
      </>)}

      {bozze.length > 0 && (
        <button type="button" style={{ ...riga, marginTop: 12 }} onClick={() => onNavigate({ page: "le-mie-bozze" })}>
          <span style={{ flex: 1 }}>📝 <strong>{bozze.length}</strong> {bozze.length === 1 ? "modulo iniziato e non salvato" : "moduli iniziati e non salvati"} su questo dispositivo</span>
          <span style={{ color: "#a78bfa" }}>›</span>
        </button>
      )}
    </div>
  );
}
